import {
  Badge,
  Button,
  Checkbox,
  DateTimePicker,
  EmptyState,
  Menu,
  type MenuItem,
  Modal,
  PageHeader,
  Skeleton,
  buttonClass,
  useConfirm,
  useToast,
} from '@/components';
import { localDateTime, validDateTime } from '@/components/DateTimePicker';
import { useAuth } from '@/features/auth/AuthProvider';
import { ResultsDashboard } from '@/features/polls/ResultsDashboard';
import { ViewersModal } from '@/features/polls/ViewersModal';
import { useClosePoll, useDeletePoll, usePollResults, useUpdatePoll } from '@/features/polls/hooks';
import { ApiClientError } from '@/lib/api';
import { A, useNavigate, useParams } from '@solidjs/router';
import { Show, createSignal } from 'solid-js';

export default function PollResults() {
  const params = useParams<{ pollId: string }>();
  const query = usePollResults(() => params.pollId);
  const { session } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const close = useClosePoll(params.pollId);
  const remove = useDeletePoll(params.pollId);
  const update = useUpdatePoll(params.pollId);
  const [viewersOpen, setViewersOpen] = createSignal(false);
  const [editingDeadline, setEditingDeadline] = createSignal(false);
  const [deadline, setDeadline] = createSignal('');
  const [reopen, setReopen] = createSignal(false);
  const poll = () => query.data?.data.poll;

  const actions = (): MenuItem[] => {
    const current = poll();
    if (!current) return [];
    const canDelete = current.canManage || (session()?.permissions.has('polls:delete') ?? false);
    const items: MenuItem[] = [];
    if (current.canManage)
      items.push(
        { label: 'Manage viewers', onSelect: () => setViewersOpen(true) },
        {
          label: 'Change deadline',
          onSelect: () => {
            setDeadline(localDateTime(current.closesAt));
            setReopen(false);
            setEditingDeadline(true);
          },
        },
      );
    if (current.status === 'open' && canDelete && session()?.permissions.has('polls:close'))
      items.push({
        label: 'Close now',
        onSelect: async () => {
          if (!(await confirm({ title: `Close ${current.title}?`, confirmLabel: 'Close' }))) return;
          await close.mutateAsync(undefined as never);
          toast.success('Poll closed');
        },
      });
    if (canDelete)
      items.push({
        label: 'Delete poll',
        danger: true,
        onSelect: async () => {
          if (
            !(await confirm({
              title: `Delete ${current.title}?`,
              body: 'This permanently deletes every response.',
              confirmLabel: 'Delete',
              danger: true,
            }))
          )
            return;
          await remove.mutateAsync(undefined as never);
          navigate('/polls');
        },
      });
    return items.map((item) => ({ ...item, disabled: close.isPending || remove.isPending }));
  };

  const saveDeadline = async () => {
    const current = poll();
    if (!current) return;
    if (reopen() && deadline() && new Date(deadline()).getTime() <= Date.now()) {
      toast.error('Choose a future deadline or no deadline to reopen the poll');
      return;
    }
    try {
      await update.mutateAsync({
        closesAt:
          deadline() === localDateTime(current.closesAt)
            ? current.closesAt
            : deadline()
              ? new Date(deadline()).toISOString()
              : null,
        ...(reopen() ? { reopen: true } : {}),
      });
      setEditingDeadline(false);
      toast.success(reopen() ? 'Poll reopened' : 'Deadline updated');
    } catch (error) {
      toast.error(
        error instanceof ApiClientError ? error.error.message : 'Could not update deadline',
      );
    }
  };

  return (
    <section>
      <PageHeader
        title={poll()?.title ?? 'Poll results'}
        sub={
          <Show when={poll()}>
            {(current) => (
              <Badge tone={current().status === 'open' ? 'success' : 'neutral'}>
                {current().status}
              </Badge>
            )}
          </Show>
        }
        actions={
          <div class="flex gap-2">
            <A href={`/polls/${params.pollId}`}>
              <Button variant="secondary">Back to poll</Button>
            </A>
            <Show when={actions().length}>
              <Menu
                trigger={
                  <>
                    Actions <span aria-hidden="true">▾</span>
                  </>
                }
                triggerClass={buttonClass('secondary')}
                items={actions()}
              />
            </Show>
          </div>
        }
      />
      <div class="mx-auto max-w-5xl p-6">
        <Show when={!query.isPending} fallback={<Skeleton class="h-64" />}>
          <Show
            when={query.data?.data}
            fallback={<EmptyState variant="error" title="Results are unavailable" />}
          >
            {(results) => <ResultsDashboard results={results()} />}
          </Show>
        </Show>
      </div>
      <Modal
        open={editingDeadline()}
        onClose={() => setEditingDeadline(false)}
        title="Change deadline"
      >
        <div class="flex flex-col gap-4">
          <DateTimePicker
            label="Closes at"
            value={deadline()}
            onChange={setDeadline}
            disabled={update.isPending}
          />
          <Show when={poll()?.status === 'closed'}>
            <Checkbox
              label="เปิดรับคำตอบอีกครั้ง"
              checked={reopen()}
              onChange={setReopen}
              disabled={update.isPending}
            />
          </Show>
          <div class="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditingDeadline(false)}>
              Cancel
            </Button>
            <Button
              loading={update.isPending}
              disabled={!validDateTime(deadline())}
              onClick={saveDeadline}
            >
              Save deadline
            </Button>
          </div>
        </div>
      </Modal>
      <Show when={viewersOpen()}>
        <ViewersModal open pollId={params.pollId} onClose={() => setViewersOpen(false)} />
      </Show>
    </section>
  );
}
